import React from 'react';
import { X, Calendar, Tag, Clock } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { formatContent } from '../utils/markdownParser';

function stripContent(text) {
  if (!text) return '';
  // Strip HTML tags
  let stripped = text.replace(/<[^>]+>/g, ' ');
  // Strip markdown image tags
  stripped = stripped.replace(/\[IMG:[^\]]+\]/g, '');
  // Strip markdown formatting
  stripped = stripped
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/^[\-\*]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped;
}

function readingTime(content) {
  if (!content) return 1;
  const words = stripContent(content).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

export function DraftPreview({ post, onClose }) {
  const now = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const categories = post.categories?.length ? post.categories : (post.category ? [post.category] : []);
  const minutes = readingTime(post.content);

  return (
    <div className="fixed inset-0 z-50 bg-background overflow-y-auto" data-testid="draft-preview">
      {/* Top bar */}
      <div className="sticky top-0 bg-background/80 backdrop-blur border-b border-border z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
            Draft Preview
          </span>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-preview-btn">
            <X className="h-4 w-4 mr-1" />
            Close Preview
          </Button>
        </div>
      </div>

      {/* Post content — mirrors PostPage layout */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <header className="mb-8">
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {categories.map((cat) => (
                <Badge key={cat} variant="secondary">
                  <Tag className="h-3 w-3 mr-1" />
                  {cat}
                </Badge>
              ))}
            </div>
          )}

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mb-4">
            {post.title || 'Untitled Post'}
          </h1>

          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              {now}
            </span>
            <span className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              {minutes} min read
            </span>
          </div>
        </header>

        {post.featured_image && (
          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={post.featured_image.startsWith('/api')
                ? `${process.env.REACT_APP_BACKEND_URL}${post.featured_image}`
                : post.featured_image}
              alt={post.title}
              className="w-full h-auto"
            />
          </div>
        )}

        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {post.tags.map((tag) => (
              <Badge key={tag} variant="outline">
                <Tag className="h-3 w-3 mr-1" />
                {tag}
              </Badge>
            ))}
          </div>
        )}

        <div
          className="prose prose-lg dark:prose-invert max-w-none"
          data-testid="preview-content"
          dangerouslySetInnerHTML={{ __html: formatContent(post.content) }}
        />
      </article>
    </div>
  );
}
